import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { get, del } from '../../utils/fetchAPI';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import PageTitle from "../../components/page-title/pageTitle";
import { RiShieldLine } from "react-icons/ri";
import { AnimatePresence, motion } from "framer-motion";
import PermissionForm from "./permission-form";
import Loader from "../../components/loader/loder";
import { swalDel } from "../../utils/SwalUti";
import { toast } from "react-toastify";

const PermissionList = () => {
    const [permissions, setPermissions] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState({});
    const [selectedPermission, setSelectedPermission] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [defaultModule, setDefaultModule] = useState(null);

    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        setIsLoading(true);
        try {
            const response = await get('/permission/list');
            const permissionsList = response?.data?.permissions || [];
            setPermissions(permissionsList);
            
            // Group permissions by module
            const grouped = permissionsList.reduce((acc, permission) => {
                const module = permission.module || 'other';
                if (!acc[module]) {
                    acc[module] = [];
                }
                acc[module].push(permission);
                return acc;
            }, {});
            setGroupedPermissions(grouped);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            toast.error('Failed to fetch permissions');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = (module = null) => {
        setSelectedPermission(null);
        setDefaultModule(module);
        setShowModal(true);
    };

    const handleEdit = (permission) => {
        setSelectedPermission(permission);
        setShowModal(true);
    };

    const handleDelete = async (permissionId, permissionName) => {
        const result = await swalDel(`Are you sure you want to delete "${permissionName}"? This action cannot be undone.`);
        
        if (result.isConfirmed) {
            try {
                await del(`/permission/${permissionId}`);
                toast.success('Permission deleted successfully!', {
                    position: "top-center",
                    theme: "light",
                });
                fetchPermissions();
            } catch (error) {
                console.error('Error deleting permission:', error);
                toast.error('Failed to delete permission. Please try again.', {
                    position: "top-center",
                    theme: "light",
                });
            }
        }
    };

    const handleFormSuccess = () => {
        fetchPermissions();
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedPermission(null);
        setDefaultModule(null);
    };

    const columns = [
        {
            name: '#',
            selector: (row, index) => index + 1,
            sortable: true,
            width: '70px',
            center: true,
        },
        {
            name: 'Name',
            selector: row => row.name || 'N/A',
            sortable: true,
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Slug',
            selector: row => row.slug || 'N/A',
            sortable: true,
            wrap: true,
            minWidth: '180px',
        },
        {
            name: 'Type',
            selector: row => row.type || 'N/A',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Status',
            selector: row => row.status || 'N/A',
            sortable: true,
            width: '120px',
            cell: (row) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    row.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                }`}>
                    {row.status || 'N/A'}
                </span>
            ),
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex gap-2">
                    <button
                        className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                    >
                        <FaEdit />
                    </button>
                    <button
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        onClick={() => handleDelete(row._id, row.name)}
                        title="Delete"
                    >
                        <FaTrash />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px',
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
        rows: {
            style: {
                fontSize: '0.875rem',
                minHeight: '56px',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
                cursor: 'pointer',
            },
        },
        cells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-full">
                <Loader />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5 w-full px-2 sm:px-0">
            {/* Header */}
            <div className="flex justify-between items-center">
                <PageTitle title="Permissions" icon={<RiShieldLine />} />
            </div>

            {/* Grouped Permissions by Module */}
            {Object.keys(groupedPermissions).length === 0 ? (
                <div className="bg-white rounded-md p-10 text-center rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)]">
                    <p className="text-gray-500 text-lg">No permissions found</p>
                    <p className="text-gray-400 text-sm mt-2 mb-4">Create your first permission to get started</p>
                    <button
                        onClick={() => handleCreate()}
                        className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors mx-auto"
                    >
                        <FaPlus className="w-4 h-4" />
                        Create New Permission
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {Object.keys(groupedPermissions).sort().map((module) => (
                        <div key={module} className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                            {/* Module Header */}
                            <div className="bg-gradient-to-r from-primary to-teal-600 px-5 py-3 flex justify-between items-center">
                                <h2 className="text-white font-bold text-lg capitalize">
                                    {module} Module
                                    <span className="ml-2 text-sm font-normal opacity-90">
                                        ({groupedPermissions[module].length} {groupedPermissions[module].length === 1 ? 'permission' : 'permissions'})
                                    </span>
                                </h2>
                                <button
                                    onClick={() => handleCreate(module)}
                                    className="flex items-center justify-center gap-2 text-sm px-3 py-1.5 bg-white text-primary rounded-md hover:bg-gray-100 transition-colors font-medium"
                                >
                                    <FaPlus className="w-3 h-3" />
                                    Add Permission
                                </button>
                            </div>
                            
                            {/* Permissions Table for this Module */}
                            <DataTable
                                columns={columns}
                                data={groupedPermissions[module]}
                                customStyles={customStyles}
                                highlightOnHover
                                striped
                                noDataComponent={
                                    <div className="text-center py-10 text-gray-500">
                                        No permissions in this module
                                    </div>
                                }
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            className="bg-white p-5 rounded-md shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        >
                            <PermissionForm
                                permission={selectedPermission}
                                defaultModule={defaultModule}
                                onClose={handleCloseModal}
                                onSuccess={handleFormSuccess}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PermissionList;

